// src/components/layers/LayerImportDialog.jsx
//
// QGIS-style sublayer picker for the My Layers importer. Shown after Phase 1
// (inspect) when a file has >1 sublayer. Each checked row becomes its own
// imported layer (Phase 3). Empty layers are disabled; mixed-geometry layers
// offer a "split by geometry type" option (one layer per geometry family).
//
// Selector between inspect and import is the sublayer INDEX (names can collide).

import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { useUserLayersStore } from "../../stores/useUserLayersStore";

const DEFAULT_COLOR = "#1f9dff"; // matches layerColor("imported")

function geometryLabel(sub) {
  const entries = Object.entries(sub.geometry_histogram ?? {});
  if (entries.length === 0) return "—";
  return entries.map(([fam, n]) => `${fam} (${n})`).join(", ");
}

export default function LayerImportDialog({ sublayers, uploadToken, defaultBaseName, onImported, onClose }) {
  const importSelectedLayers = useUserLayersStore((s) => s.importSelectedLayers);
  const importing = useUserLayersStore((s) => s.importing);

  // Per-index row state, seeded from the inspect result. Empty layers start
  // unchecked and stay disabled.
  const [rows, setRows] = useState(() => {
    const map = {};
    for (const s of sublayers) {
      map[s.index] = {
        checked: !s.empty,
        name: s.name || defaultBaseName || `Layer ${s.index + 1}`,
        color: DEFAULT_COLOR,
        split: false,
      };
    }
    return map;
  });
  const [error, setError] = useState("");

  const selectable = useMemo(() => sublayers.filter((s) => !s.empty), [sublayers]);
  const checkedCount = selectable.filter((s) => rows[s.index]?.checked).length;
  const allChecked = checkedCount === selectable.length && selectable.length > 0;

  const patch = (index, p) => setRows((r) => ({ ...r, [index]: { ...r[index], ...p } }));
  const setAll = (checked) =>
    setRows((r) => {
      const next = { ...r };
      for (const s of selectable) next[s.index] = { ...next[s.index], checked };
      return next;
    });

  async function handleAdd() {
    const selections = sublayers
      .filter((s) => !s.empty && rows[s.index]?.checked)
      .map((s) => {
        const row = rows[s.index];
        return {
          index: s.index,
          name: row.name?.trim() || s.name,
          color: row.color,
          split_by_geometry: !!(s.mixed_geometry && row.split),
        };
      });

    if (selections.length === 0) {
      setError("Select at least one layer to import.");
      return;
    }
    setError("");

    try {
      const result = await importSelectedLayers(uploadToken, selections);
      onImported?.(result);
    } catch (err) {
      setError(err?.message || "Import failed.");
    }
  }

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm"
         onClick={(e) => e.target === e.currentTarget && !importing && onClose?.()}>
      <div className="w-full max-w-2xl bg-bg-soft border border-slate-700 rounded-xl shadow-2xl flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-800">
          <div>
            <h2 className="text-sm font-semibold text-gray-100">Select layers to import</h2>
            <p className="text-[11px] text-fg-secondary mt-0.5">
              {sublayers.length} sublayers found · {checkedCount} selected
            </p>
          </div>
          <button onClick={() => !importing && onClose?.()} className="text-fg-secondary hover:text-slate-300 transition">
            <X size={16} />
          </button>
        </div>

        {/* Table */}
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-xs">
            <thead className="bg-bg-softer text-fg-secondary sticky top-0">
              <tr>
                <th className="w-8 px-3 py-2 text-left">
                  <input type="checkbox" checked={allChecked}
                         onChange={(e) => setAll(e.target.checked)}
                         aria-label="Select all" className="accent-accent-info" />
                </th>
                <th className="text-left px-2 py-2 font-medium">Layer name</th>
                <th className="text-left px-2 py-2 font-medium">Geometry</th>
                <th className="text-right px-2 py-2 font-medium">Features</th>
                <th className="text-left px-2 py-2 font-medium">CRS</th>
                <th className="w-10 px-2 py-2 font-medium">Color</th>
              </tr>
            </thead>
            <tbody>
              {sublayers.map((s) => {
                const row = rows[s.index];
                const disabled = s.empty;
                return (
                  <tr key={s.index}
                      className={`border-t border-slate-800 ${disabled ? "opacity-40" : "hover:bg-bg-softer/50"}`}>
                    <td className="px-3 py-1.5">
                      <input type="checkbox" disabled={disabled} checked={!!row?.checked}
                             onChange={(e) => patch(s.index, { checked: e.target.checked })}
                             aria-label={`Select ${s.name}`} className="accent-accent-info" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" value={row?.name ?? ""} disabled={disabled}
                             onChange={(e) => patch(s.index, { name: e.target.value })}
                             maxLength={80}
                             className="w-full bg-bg border border-slate-700 rounded px-1.5 py-1 text-[11px] text-gray-200 focus:border-accent-info/60 outline-none disabled:opacity-60" />
                      {s.mixed_geometry && !disabled && (
                        <label className="flex items-center gap-1.5 mt-1 text-[10px] text-fg-secondary cursor-pointer">
                          <input type="checkbox" checked={!!row?.split}
                                 onChange={(e) => patch(s.index, { split: e.target.checked })}
                                 className="accent-accent-info" />
                          Split by geometry type
                        </label>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-fg-secondary">
                      {geometryLabel(s)}
                      {s.empty && <span className="ml-1 text-[10px] italic">empty</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-gray-300">
                      {s.feature_count?.toLocaleString() ?? 0}
                    </td>
                    <td className="px-2 py-1.5 text-fg-secondary truncate max-w-[90px]" title={s.srs ?? ""}>
                      {s.srs ?? "—"}
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="color" value={row?.color ?? DEFAULT_COLOR} disabled={disabled}
                             onChange={(e) => patch(s.index, { color: e.target.value })}
                             aria-label={`Color for ${s.name}`}
                             className="w-6 h-6 rounded cursor-pointer bg-transparent border border-slate-700 disabled:opacity-50" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-800 flex items-center gap-3">
          {error && <p className="text-[11px] text-red-400 flex-1">{error}</p>}
          <div className="ml-auto flex gap-2">
            <button onClick={() => !importing && onClose?.()}
                    className="px-3 py-1.5 text-xs rounded-md border border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600 transition">
              Cancel
            </button>
            <button onClick={handleAdd} disabled={importing || checkedCount === 0}
                    className={`px-4 py-1.5 text-xs rounded-md font-medium transition ${
                      importing || checkedCount === 0
                        ? "bg-accent-action/10 border border-accent-action/20 text-accent-action/40 cursor-not-allowed"
                        : "bg-accent-action/20 border border-accent-action/50 text-accent-action hover:bg-accent-action/30"}`}>
              {importing ? "Importing…" : `Add ${checkedCount || ""}`.trim()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
